import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { validateEnv, env, isDevelopment } from '@utils/env';
import { initializeMsal } from '@auth/msalInstance';
import { registerMsalEventCallbacks, syncExistingSession } from '@auth/msalEventHandler';
import { store } from '@store';
import { initializeCorrelation, initializeTelemetry } from '@/services';
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error(
    'Failed to find the root element. Ensure index.html contains a div with id="root".'
  );
}

try {
  validateEnv();

  if (isDevelopment()) {
    const config = env();
    /* eslint-disable no-console */
    console.log(`🚀 GEM Frontend starting in ${config.envName} mode`);
    console.log(`📡 API Base URL: ${config.apiBaseUrl}`);
    if (config.debugMode) {
      console.log('🐛 Debug mode enabled');
    }
    /* eslint-enable no-console */
  }
} catch (error) {
  rootElement.innerHTML = `
    <div style="padding: 1.25rem; max-width: 37.5rem; margin: 2.5rem auto;">
      <h1 style="color: #d32f2f;">Configuration Error</h1>
      <p>The application could not start due to missing environment configuration.</p>
      <pre style="background: #f5f5f5; padding: 1rem; border-radius: 0.25rem; overflow-x: auto;">${
        error instanceof Error ? error.message : 'Unknown error'
      }</pre>
      <p>Please check the console for more details.</p>
    </div>
  `;

  console.error('Error during application startup:', error);
}

async function initializeApp(): Promise<void> {
  if (!rootElement) {
    return;
  }

  try {
    initializeCorrelation();

    initializeTelemetry();

    await initializeMsal();

    registerMsalEventCallbacks(store as Parameters<typeof registerMsalEventCallbacks>[0]);

    syncExistingSession(store as Parameters<typeof syncExistingSession>[0]);

    createRoot(rootElement).render(
      <StrictMode>
        <App />
      </StrictMode>
    );
  } catch (error) {
    console.error('Failed to initialize MSAL:', error);
    if (rootElement) {
      rootElement.innerHTML = `
        <div style="padding: 1.25rem; max-width: 37.5rem; margin: 2.5rem auto;">
          <h1 style="color: #d32f2f;">Authentication Error</h1>
          <p>Failed to initialize authentication. Please refresh the page or contact support.</p>
          <pre style="background: #f5f5f5; padding: 1rem; border-radius: 0.25rem; overflow-x: auto;">${
            error instanceof Error ? error.message : 'Unknown error'
          }</pre>
        </div>
      `;
    }
  }
}

void initializeApp();
